/**
 * @fileoverview
 * Restores latest player state for a Soft PIP experience. This is
 * executed at the page load (fwnInit) and the persisted player state
 * is processed only once (`isSoftPIPStateProcessed` boolean)
 */
import { addActivityDetector } from '@core/activityDetector';
import { getFWN } from '@core/fwn';
import { logError, logInfo } from '@core/logging';
import { SOFT_PIP_PLAYER_STATE_KEY } from '@core/pictureInPicture/constants';
import { isInPIPIframe } from '@core/pictureInPicture/isInPIPIframe';
import { getFromHistoryState } from '@core/storage/historyState';
import { AppContextType } from '@embed/components/helpers';
import { uuidv4 } from '@embed/helpers';
import { IS_SOFT_PIP_STATE_PROCESSED } from './constants';
import { getPersistedPlayerState } from './storage';
/**
 * Restore player state for a Soft PIP experience and initiate programmatic player (minimized).
 */
export function restoreSoftPIPPlayer() {
    const fwn = getFWN();
    if (!fwn) {
        return;
    }
    try {
        if (!fwn[IS_SOFT_PIP_STATE_PROCESSED] && !isInPIPIframe()) {
            let lastPlayerState = {
                appContextType: AppContextType.EMBED_CONTEXT_TYPE,
                createdAt: 0,
            };
            try {
                lastPlayerState =
                    getPersistedPlayerState() || {};
            }
            catch (e) { }
            fwn[IS_SOFT_PIP_STATE_PROCESSED] = true;
            const { createdAt, ...storedState } = lastPlayerState;
            // Last player state is valid for 20s
            if (!createdAt || createdAt + 20 * 1000 < new Date().getTime()) {
                logInfo('Expired player state (TTL 20s)');
                return;
            }
            if (fwn.play) {
                /**
                 * Retrieve the minimized state from history state to restore the player
                 * to the same layout as it was left in case user returns to the same page.
                 * If not present, default to true.
                 */
                const minimized = getFromHistoryState(SOFT_PIP_PLAYER_STATE_KEY)?.minimized !==
                    undefined
                    ? getFromHistoryState(SOFT_PIP_PLAYER_STATE_KEY).minimized
                    : true;
                fwn.play({
                    ...storedState,
                    minimized: minimized,
                    widget_id: uuidv4(),
                    show_preroll: false,
                }, {
                    // Lets try to get unmuted/autoplay. If the attempt
                    // fails, globalVideoElement will take care of graceful
                    // degradation to muted/autoplay
                    autoplay: true,
                    // pass the stored muted state
                    // isUseMuted is tracked in the video player store
                    // so it's not persisted between page loads
                    muted: storedState.muted ?? false,
                    pip: true,
                });
            }
            // If previously stored player state was unmuted, we will try
            // to unmute again by listening to first user interaction
            // with the document (click, scroll). Attempt to unmuting on
            // iOS will pause the video, so triggering `play()` will ensure
            // that the video continues to play (muted though).
            if (storedState.muted === false) {
                addActivityDetector(() => {
                    if (fwn.player.muted) {
                        fwn.player.muted = false;
                        fwn.player.play();
                    }
                }, ['click', 'scroll']);
            }
        }
        else {
            logInfo('Expired player state (TTL 20s)');
        }
    }
    catch (error) {
        if (error instanceof Error) {
            logError(error);
        }
    }
}
